import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, ArrowRight, ArrowDown, Play, Disc3, Mic2, Sparkles, Gem, CalendarDays, Users, ShieldCheck, Quote, Star, Instagram, Mail, Phone, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ARROW = "◥◣";

const NAV_LINKS = [
  { label: "Home", href: "#top" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Meet", href: "#work" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  {
    icon: Disc3,
    title: "Weddings",
    blurb:
      "From your first dance to the final track of the night, KLIQE crafts the soundtrack and atmosphere of your day — start to finish.",
    image: "/images/wedding.png",
  },
  {
    icon: Mic2,
    title: "Private Parties",
    blurb:
      "Birthdays, anniversaries, milestone moments — no two parties are the same. We build a bespoke setlist and production every time.",
    image: "/images/svc-02.png",
  },
  {
    icon: Sparkles,
    title: "Corporate & Brand Events",
    blurb:
      "Galas, premieres, launches and activations — engineered for impact. The kind of nights people are still talking about Monday morning.",
    image: "/images/svc-03.png",
  },
];

const STATS = [
  { icon: Gem, value: "12+", label: "Years Building" },
  { icon: CalendarDays, value: "41", label: "Cities Staged" },
  { icon: Users, value: "09", label: "Projects / Season" },
  { icon: ShieldCheck, value: "100%", label: "By Referral" },
];

const REVIEWS = [
  {
    name: "Sophie & Dan",
    type: "Wedding",
    quote:
      "KLIQE completely transformed our wedding. The DJ and sax combo were insane. Our guests haven't stopped talking about it.",
  },
  {
    name: "James M.",
    type: "Corporate Event",
    quote:
      "Professional, seamless and worth every penny. The DJ made our conference after-party. Never before have our attendees stayed, laughed, and danced the entire night. It was the best event our company has ever held. 10/10.",
  },
  {
    name: "Olivia R.",
    type: "Private Party",
    quote:
      "The energy, the live percussion, the DJ… unreal. KLIQE brought a festival level energy to the party. It made the night. Couldn't of asked for more.",
  },
];

const CLIENTS = [
  "MOËT HENNESSY",
  "MIDDLETON LODGE ESTATE",
  "BACARDI",
  "CHICHELEY HALL",
  "MNKY HSE",
  "STANDON HALL",
  "FOX HOUSE",
  "COLSHAW HALL",
  "STIRK HOUSE",
  "186 MCR",
];

const WORK = [
  {
    title: "Live DJs",
    type: "The Decks",
    image: "/images/live-djs.png",
  },
  {
    title: "Live Musicians",
    type: "Sax · Strings · Percussion",
    image: "/images/live-musicians.avif",
  },
  {
    title: "Media Capture",
    type: "Photo & Film",
    image: "/images/media-capture.png",
  },
  {
    title: "Sound & Lighting",
    type: "Production",
    image: "/images/sound-lighting.png",
  },
];

const ABOUT_IMAGE = "/images/about.png";
const REVIEWS_IMAGE =
  "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1400&q=80";

const Header = () => (
  <header
    data-testid="site-header"
    className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/55 border-b border-white/10"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
      <a
        href="#top"
        data-testid="brand-logo"
        className="font-display text-2xl tracking-tight text-white inline-flex items-center gap-2"
      >
        KLIQE
        <span className="text-[#A076A8] text-sm font-mono">{ARROW}</span>
      </a>
      <nav className="hidden lg:flex items-center gap-7">
        {NAV_LINKS.map((l) => (
          <a
            key={l.label}
            href={l.href}
            data-testid={`nav-link-${l.label.toLowerCase()}`}
            className="font-mono text-[0.7rem] tracking-[0.22em] uppercase text-white/85 hover:text-white transition-colors"
          >
            {l.label}
          </a>
        ))}
      </nav>
      <a
        href="#contact"
        data-testid="header-cta"
        className="group inline-flex items-center gap-2 font-mono text-[0.7rem] tracking-[0.22em] uppercase text-white bg-[#4A0E4E] hover:bg-[#6B1571] rounded-full px-5 py-2.5 transition-colors"
      >
        Enquire Now
        <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
    </div>
  </header>
);

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-screen w-full overflow-hidden bg-[#050505]"
  >
    {/* Full-bleed background media — landscape video for desktop, portrait for mobile */}
    <div className="absolute inset-0 z-0">
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="hero-video-desktop"
        className="hidden md:block absolute inset-0 w-full h-full object-cover bg-black"
      >
        <source src="/videos/hero-desktop.mp4" type="video/mp4" />
      </video>
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="hero-video-mobile"
        className="block md:hidden absolute inset-0 w-full h-full object-cover bg-black"
      >
        <source src="/videos/hero-mobile.mp4" type="video/mp4" />
      </video>
      {/* Readability overlays — lighter on mobile so the video reads brighter */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40 md:bg-gradient-to-r md:from-black/20 md:via-black/15 md:to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505] md:from-black/10" />
      <div className="absolute inset-0 stripes opacity-0 md:opacity-10" />
    </div>

    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-20 min-h-screen flex flex-col justify-center">
      <div className="max-w-3xl">
        <p className="overline mb-6 rise-in">Premium Event Entertainment {ARROW}</p>
        <h1
          data-testid="hero-headline"
          className="font-display uppercase text-white leading-[0.88] tracking-[-0.02em] rise-in rise-delay-1
                     text-[18vw] sm:text-[14vw] lg:text-[10rem] drop-shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
        >
          KLIQE
          <span className="text-[#A076A8]">.</span>
        </h1>
        <p className="mt-6 font-display uppercase text-2xl sm:text-3xl lg:text-4xl text-white leading-[1.05] tracking-tight max-w-2xl rise-in rise-delay-2 drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
          Event entertainment that feels like a
          <span className="text-[#A076A8]"> headline performance.</span>
        </p>
        <div className="mt-9 flex items-center gap-5 flex-wrap rise-in rise-delay-3">
          <a
            href="#services"
            data-testid="hero-cta-services"
            className="group inline-flex items-center gap-3 font-mono text-[0.72rem] tracking-[0.28em] uppercase bg-[#4A0E4E] hover:bg-[#6B1571] text-white rounded-full px-7 py-4 transition-colors"
          >
            Discover Our Services
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => {
  const items = ["From Day To Night", "Premium Entertainment", "Brand Activations", "Weddings", "Private Parties", "Corporate Events"];
  const stream = [...items, ...items, ...items];
  return (
    <div
      data-testid="marquee"
      className="relative overflow-hidden border-y border-white/10 bg-[#050505]"
    >
      <div className="marquee-track flex gap-10 py-5 whitespace-nowrap items-center">
        {stream.map((w, i) => (
          <span
            key={i}
            className="font-display uppercase text-2xl sm:text-3xl text-white inline-flex items-center gap-10"
          >
            {w}
            <span className="text-[#A076A8] text-base font-mono">{ARROW}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const Clients = () => (
  <section
    data-testid="clients-section"
    className="relative bg-[#050505] py-10 border-b border-white/10"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <p className="overline text-center mb-6">We have performed for {ARROW}</p>
      <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
        {CLIENTS.map((c) => (
          <span
            key={c}
            data-testid={`client-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className="font-display uppercase text-lg sm:text-xl tracking-wide text-white/60 hover:text-white transition-colors"
          >
            {c}
          </span>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="relative pt-6 pb-24 sm:pt-8 sm:pb-32 bg-[#050505]"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-6 order-2 lg:order-1">
        <p className="overline">About KLIQE {ARROW}</p>
        <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-5 text-white">
          Most agencies book performers.
          <br />
          <span className="text-[#A076A8]">We build shows.</span>
        </h2>
        <p className="mt-7 text-white/65 text-base leading-relaxed max-w-xl">
          KLIQE is a collective of some of the UK's most sought-after DJs,
          musicians, and live performers.
        </p>
        <p className="mt-5 text-white/65 text-base leading-relaxed max-w-xl">
          We don't simply manage artist bookings. We design shows. From
          choreographed live moments and seamless musical transitions to
          crowd interaction and performance flow, every detail is carefully
          considered and planned to create an entertainment experience that
          feels effortless, engaging, and uniquely yours.
        </p>
        <p className="mt-5 text-white/65 text-base leading-relaxed max-w-xl">
          Our artist-first model means we invest in performers, not excessive
          agency markups. By paying our artists industry-leading rates, we're
          able to attract exceptional talent and build line-ups with genuine
          chemistry, resulting in stronger performances, packed dancefloors,
          and unforgettable moments for your guests.
        </p>
        <p className="mt-5 text-white/65 text-base leading-relaxed max-w-xl">
          Founded and led by working performers, we understand what creates
          exceptional events because we're the people delivering them. That
          means expert guidance throughout the planning process, attention to
          every detail, and the confidence that your entertainment is in
          experienced hands.
        </p>
        <p className="font-display uppercase text-xl sm:text-2xl text-white mt-7 leading-snug max-w-xl">
          Curated talent. <span className="text-[#A076A8]">Real chemistry.</span> Headline energy.
        </p>
        <p className="mt-6 font-display uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-none tracking-tight">
          This is <span className="text-[#A076A8]">KLIQE.</span>
        </p>
        <div className="mt-9 flex items-center gap-5 flex-wrap">
          <a
            href="#contact"
            data-testid="about-cta-contact"
            className="group inline-flex items-center gap-3 font-mono text-[0.72rem] tracking-[0.28em] uppercase bg-[#4A0E4E] hover:bg-[#6B1571] text-white rounded-full px-6 py-3.5 transition-colors"
          >
            Contact Us
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      <div className="lg:col-span-6 order-1 lg:order-2 relative">
        <div className="svc-card relative aspect-square overflow-hidden border border-white/10 group">
          <img
            src={ABOUT_IMAGE}
            alt="KLIQE studio at work"
            data-testid="about-image"
            className="svc-image absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/30 via-transparent to-transparent" />
        </div>
        <div className="absolute -bottom-4 -right-4 hidden lg:block w-32 h-32 stripes border border-white/10" />
      </div>
    </div>
  </section>
);

const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative pt-6 pb-24 sm:pt-8 sm:pb-32 bg-[#050505] border-t border-white/10"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-end">
        <div className="lg:col-span-7">
          <p className="overline mb-5">Our Services {ARROW}</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white">
            Entertainment that
            <br />
            <span className="text-[#A076A8]">makes an impact.</span>
          </h2>
        </div>
        <div className="lg:col-span-5 flex flex-col gap-5">
          <p className="text-white/65 leading-relaxed">
            From premium sound and lighting rigs to professional media capture,
            we can provide everything needed to make your event look, sound and
            feel unforgettable.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <article
              key={s.title}
              data-testid={`service-card-${i + 1}`}
              className="svc-card group relative bg-[#0a0a0a] border border-white/10 hover:border-[#A076A8]/60 transition-colors duration-500 flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="svc-image absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex justify-center pb-5">
                  <span className="w-12 h-12 inline-flex items-center justify-center rounded-full bg-[#4A0E4E] border border-[#A076A8]/40">
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                </div>
              </div>
              <div className="p-7 flex flex-col gap-4 flex-1 text-center">
                <h3 className="font-display uppercase text-2xl leading-none tracking-tight text-white">
                  {s.title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{s.blurb}</p>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section
    data-testid="stats-section"
    className="relative py-14 bg-gradient-to-r from-[#1a0820] via-[#2a0a30] to-[#1a0820] border-y border-[#A076A8]/20"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-8">
      {STATS.map((s) => {
        const Icon = s.icon;
        return (
          <div
            key={s.label}
            data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex items-center gap-4 justify-center lg:justify-start"
          >
            <span className="w-12 h-12 inline-flex items-center justify-center rounded-full bg-black/40 border border-[#A076A8]/30 shrink-0">
              <Icon className="w-5 h-5 text-[#C9A0CE]" />
            </span>
            <div>
              <div className="font-display uppercase text-3xl sm:text-4xl text-white leading-none">
                {s.value}
              </div>
              <div className="overline mt-2">{s.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

const Work = () => (
  <section
    id="work"
    data-testid="work-section"
    className="relative pt-6 pb-24 sm:pt-8 sm:pb-32 bg-[#050505]"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="mb-10">
        <p className="overline mb-5">Meet The KLIQE {ARROW}</p>
        <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white">
          Meet
          <br />
          <span className="text-[#A076A8]">The KLIQE.</span>
        </h2>
        <p className="mt-6 text-white/65 max-w-xl text-base leading-relaxed">
          The artists, operators and producers behind every KLIQE event,
          curated, rehearsed and ready to deliver.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {WORK.map((w, i) => (
          <div
            key={w.title}
            data-testid={`work-card-${i + 1}`}
            className="svc-card group relative aspect-[4/5] overflow-hidden border border-white/10 hover:border-[#A076A8]/60 transition-colors duration-500"
          >
            <img src={w.image} alt={w.title} className="svc-image absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between gap-4">
              <div>
                <p className="font-mono text-[0.68rem] tracking-[0.22em] uppercase text-[#A076A8]">{w.type}</p>
                <h3 className="font-display uppercase text-2xl leading-tight text-white mt-1">{w.title}</h3>
              </div>
              <ArrowUpRight className="w-5 h-5 text-white/70 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Reviews = () => (
  <section
    id="reviews"
    data-testid="reviews-section"
    className="relative pt-6 pb-24 sm:pt-8 sm:pb-32 bg-[#050505] border-t border-white/10 overflow-hidden"
  >
    <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block opacity-30">
      <img src={REVIEWS_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover svc-image" />
      <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#050505]/60 to-[#050505]" />
    </div>

    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
      <p className="overline mb-5">Client Reviews {ARROW}</p>
      <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white max-w-3xl">
        Unforgettable moments.
        <br />
        <span className="text-[#A076A8]">Real feedback.</span>
      </h2>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
        {REVIEWS.map((r, i) => (
          <article
            key={r.name}
            data-testid={`review-card-${i + 1}`}
            className="relative bg-[#0a0a0a] border border-white/10 hover:border-[#A076A8]/60 transition-colors duration-500 p-7 flex flex-col gap-5"
          >
            <div className="flex items-center justify-between">
              <Quote className="w-7 h-7 text-[#A076A8]" />
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} className="w-3.5 h-3.5 fill-[#A076A8] text-[#A076A8]" />
                ))}
              </div>
            </div>
            <p className="text-white/85 text-base leading-relaxed">"{r.quote}"</p>
            <div className="mt-auto pt-5 border-t border-white/10">
              <p className="font-display uppercase text-lg text-white tracking-tight">{r.name}</p>
              <p className="overline mt-1">{r.type}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const CTAStrip = () => (
  <section
    data-testid="cta-strip"
    className="relative py-14 bg-gradient-to-r from-[#2a0a30] via-[#3a0e44] to-[#2a0a30] border-y border-[#A076A8]/20"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
      <h3 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight text-white max-w-xl">
        Let's create something
        <br />
        <span className="text-[#C9A0CE]">unforgettable.</span>
      </h3>
      <div className="flex flex-wrap items-center gap-8">
        {[
          { icon: Gem, label: "Premium Talent" },
          { icon: Users, label: "Bespoke Production" },
          { icon: ShieldCheck, label: "Headline Energy" },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3">
            <span className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-black/40 border border-[#A076A8]/30">
              <Icon className="w-4 h-4 text-[#C9A0CE]" />
            </span>
            <span className="font-mono text-[0.7rem] tracking-[0.22em] uppercase text-white/90">{label}</span>
          </div>
        ))}
        <a
          href="#contact"
          data-testid="cta-strip-button"
          className="inline-flex items-center gap-3 font-mono text-[0.72rem] tracking-[0.28em] uppercase bg-white text-black hover:bg-[#A076A8] hover:text-white rounded-full px-6 py-3.5 transition-colors"
        >
          Enquire Now
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  </section>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    event_date: "",
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.email ||
      !form.phone ||
      !form.event_type ||
      !form.event_date ||
      !form.location ||
      !form.message
    ) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Got it — we'll reply within 48 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        event_type: "",
        event_date: "",
        location: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send. Please retry.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative pt-6 pb-24 sm:pt-8 sm:pb-32 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <p className="overline mb-5">Work with us {ARROW}</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white">
            Work with
            <span className="text-[#A076A8]"> us.</span>
          </h2>
          <p className="mt-6 text-white/65 max-w-xl mx-auto">
            Planning an event? Send the form below and we will reply, usually
            within 48 hours.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 font-mono text-[0.72rem] tracking-[0.22em] uppercase text-white/70">
            <a href="mailto:Hello@Kliqe.co.uk" data-testid="contact-email-link" className="hover:text-white">E: Hello@Kliqe.co.uk</a>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="max-w-3xl mx-auto space-y-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-9">
            <div className="sm:col-span-2 flex flex-col">
              <label className="overline mb-3" htmlFor="name">Name<span className="text-[#A076A8] ml-1">*</span></label>
              <Input id="name" data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder="Your full name" className="kliqe-input" />
            </div>
            <div className="flex flex-col">
              <label className="overline mb-3" htmlFor="email">Email<span className="text-[#A076A8] ml-1">*</span></label>
              <Input id="email" type="email" data-testid="contact-email-input" value={form.email} onChange={update("email")} placeholder="you@studio.com" className="kliqe-input" />
            </div>
            <div className="flex flex-col">
              <label className="overline mb-3" htmlFor="phone">Phone<span className="text-[#A076A8] ml-1">*</span></label>
              <Input id="phone" data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} placeholder="+44 …" className="kliqe-input" />
            </div>

            <div className="flex flex-col">
              <label className="overline mb-3">Event Type<span className="text-[#A076A8] ml-1">*</span></label>
              <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}>
                <SelectTrigger data-testid="contact-event-type-trigger" className="kliqe-input justify-between">
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent data-testid="contact-event-type-content" className="bg-[#0a0a0a] border border-white/15 text-white rounded-none font-mono text-sm">
                  <SelectItem value="Wedding" data-testid="opt-wedding">Wedding</SelectItem>
                  <SelectItem value="Private Event" data-testid="opt-private">Private Event</SelectItem>
                  <SelectItem value="Corporate Event" data-testid="opt-corporate">Corporate Event</SelectItem>
                  <SelectItem value="Brand Event" data-testid="opt-brand">Brand Event</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col">
              <label className="overline mb-3" htmlFor="event_date">Event Date<span className="text-[#A076A8] ml-1">*</span></label>
              <Input id="event_date" type="date" data-testid="contact-eventdate-input" value={form.event_date} onChange={update("event_date")} className="kliqe-input" />
            </div>

            <div className="sm:col-span-2 flex flex-col">
              <label className="overline mb-3" htmlFor="location">Location<span className="text-[#A076A8] ml-1">*</span></label>
              <Input id="location" data-testid="contact-location-input" value={form.location} onChange={update("location")} placeholder="e.g. Cheshire · UK" className="kliqe-input" />
            </div>

            <div className="sm:col-span-2 flex flex-col">
              <label className="overline mb-3" htmlFor="message">Additional Information<span className="text-[#A076A8] ml-1">*</span></label>
              <Textarea id="message" data-testid="contact-message-input" value={form.message} onChange={update("message")} placeholder="Tell us about the event — guests, vibe, music taste, anything else we should know…" rows={5} className="kliqe-input resize-none" />
            </div>

            <div className="sm:col-span-2 flex items-center gap-3 hidden">
              <Checkbox id="newsletter" data-testid="contact-newsletter-checkbox" className="rounded-none border-white/30" />
              <label htmlFor="newsletter" className="font-mono text-[0.72rem] tracking-[0.18em] uppercase text-white/70 cursor-pointer">Newsletter</label>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-2 border-t border-white/10">
            <p className="font-mono text-[0.7rem] tracking-[0.22em] uppercase text-white/45">Reply within 48h · Discretion guaranteed</p>
            <Button type="submit" disabled={submitting} data-testid="contact-submit-button" className="rounded-full bg-[#4A0E4E] text-white hover:bg-[#6B1571] px-10 py-7 font-mono text-[0.72rem] tracking-[0.28em] uppercase font-medium">
              {submitting ? "Sending…" : "Submit"}
              <ArrowUpRight className="w-4 h-4 ml-3" />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#050505]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
      <div className="col-span-2 md:col-span-3 lg:col-span-2">
        <a href="#top" className="font-display uppercase text-3xl text-white inline-flex items-center gap-2">
          KLIQE<span className="text-[#A076A8]">.</span>
          <span className="text-[#A076A8] text-sm font-mono">{ARROW}</span>
        </a>
        <p className="mt-4 font-mono text-[0.7rem] tracking-[0.22em] uppercase text-white/45 leading-relaxed max-w-xs">
          Premium event entertainment. From day to night.
        </p>
        <div className="mt-6 flex items-center gap-3 hidden">
          <a href="https://instagram.com/kliqe" data-testid="footer-ig" aria-label="Instagram" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-[#A076A8] text-white/70 hover:text-white transition-colors" target="_blank" rel="noreferrer">
            <Instagram className="w-4 h-4" />
          </a>
        </div>
      </div>
      <div>
        <p className="overline mb-4">Navigation</p>
        <ul className="space-y-2 font-mono text-[0.72rem] tracking-[0.18em] uppercase text-white/65">
          <li><a data-testid="footer-link-home" className="hover:text-white" href="#top">Home</a></li>
          <li><a data-testid="footer-link-about" className="hover:text-white" href="#about">About</a></li>
          <li><a data-testid="footer-link-services" className="hover:text-white" href="#services">Services</a></li>
          <li><a data-testid="footer-link-work" className="hover:text-white" href="#work">Meet The KLIQE</a></li>
          <li><a data-testid="footer-link-reviews" className="hover:text-white" href="#reviews">Reviews</a></li>
          <li><a data-testid="footer-link-contact" className="hover:text-white" href="#contact">Contact</a></li>
        </ul>
      </div>
      <div>
        <p className="overline mb-4">Services</p>
        <ul className="space-y-2 font-mono text-[0.72rem] tracking-[0.18em] uppercase text-white/65">
          <li>Weddings</li>
          <li>Private Parties</li>
          <li>Corporate & Brand</li>
          <li>Full Production</li>
        </ul>
      </div>
      <div>
        <p className="overline mb-4">Get in touch</p>
        <ul className="space-y-3 font-mono text-[0.72rem] tracking-[0.18em] uppercase text-white/65">
          <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#A076A8]" /> Hello@Kliqe.co.uk</li>
          <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#A076A8]" /> +44 7866 961 763</li>
          <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#A076A8]" /> Cheshire · UK</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 font-mono text-[0.68rem] tracking-[0.22em] uppercase text-white/40">
        <span>©  KLIQE — All rights reserved</span>
        <span>Premium event entertainment {ARROW}</span>
      </div>
    </div>
  </footer>
);

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
    );
    document.querySelectorAll(".svc-card").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="App font-body bg-[#050505] text-white">
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.12)", color: "#FAFAFA", borderRadius: 0, fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.04em", fontSize: "0.78rem" } }} />
      <Header />
      <main>
        <Hero />
        <Marquee />
        <Clients />
        <About />
        <Services />
        <Work />
        <Reviews />
        <CTAStrip />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
